import TR from 'utils/TR';
import { Chart } from 'chart.js';
import Section from 'components/common/Section';
import DIRECTION_VERTICAL from 'constants/DIRECTION_VERTICAL';
import useRect from 'hooks/common/useRect';
import React from 'react';
import T from 'utils/T';
import ex from 'utils/ex';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';

import 'chartjs-adapter-luxon';
import VBox from 'components/common/VBox';
import useReportAgpGraph from 'hooks/communication/useReportAgpGraph';

const DEFAULT_VALUE = NaN;
const columns = Array.from({ length: 25 }).map((name, index) => `${index.toString().padStart(2, '0')}00`);
const config = (hash) => ({
  type: 'line',
  data: {
    labels: columns,
    datasets: [
      {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_5per || DEFAULT_VALUE;
        }),
        pointRadius: 0,
        borderWidth: 0,
        cubicInterpolationMode: 'monotone',
        tension: 0.4,
      }, {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_95per || DEFAULT_VALUE;
        }),
        pointRadius: 0,
        borderWidth: 0,
        fill: '-1',
        cubicInterpolationMode: 'monotone',
        backgroundColor: '#347AFF20',
        tension: 0.4,
      }, {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_25per || DEFAULT_VALUE;
        }),
        pointRadius: 0,
        borderWidth: 0,
        cubicInterpolationMode: 'monotone',
        tension: 0.4,
      }, {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_75per || DEFAULT_VALUE;
        }),
        pointRadius: 0,
        borderWidth: 0,
        fill: '-1',
        cubicInterpolationMode: 'monotone',
        backgroundColor: '#347AFF20',
        tension: 0.4,
      }, {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_50per || DEFAULT_VALUE;
        }),
        pointRadius: 0,
        borderWidth: 3,
        cubicInterpolationMode: 'monotone',
        borderColor: '#2251A9',
        tension: 0.4,
      }
    ]
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        display: true,
        ticks: {
          color: '#000000',
          callback: (value) => {
            if (value % 6 === 0) {
              return value + 'H';
            } else {
              return '';
            }
          },
          stepSize: 6,
        },
        title: {
          display: true
        },
        grid: {
          drawTicks: false,
        },
      },
      y: {
        type: 'logarithmic',
        display: true,
        afterTickToLabelConversion: (scaleInstance) => {
          const ticks = scaleInstance.ticks;
          const newTicks = ticks?.filter((tick) => {
            switch (tick.value) {
              case 0: return true;
              case 54: return true;
              case 70: return true;
              case 180: return true;
              case 250: return true;
              case 350: return true;
              default: return false;
            }
          });
          scaleInstance.ticks = newTicks;
        },
        title: {
          display: true,
        },
        min: 40,
        max: 600,
        ticks: {
          callback: (value) => {
            switch (value) {
              case 0:
              case 54:
              case 70:
              case 180:
              case 250:
              case 350:
                return value;
              default:
                return '';
            }
          },
          // stepSize: 1,
          // display: false,
        },
        afterBuildTicks: (chart) => {
          chart.ticks = [];
          chart.ticks.push({ value: 54 });
          chart.ticks.push({ value: 70 });
          chart.ticks.push({ value: 180 });
          chart.ticks.push({ value: 250 });
          chart.ticks.push({ value: 350 });
        },
        grid: {
          drawTicks: false,
        },
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: function (context) {
            const label = (context[0].label || '').split('');
            label.splice(2, 0, ':');
            return label.join('');
          }
        },
      },
    },
  },
});

export default function SimulationChart(props) {
  const {
    agp: pAgp,
    ...rest
  } = props;

  const [agp, setAgp] = React.useState(() => []);
  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();

  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);

  const {
    requestReportAgpGraph,
  } = useReportAgpGraph();

  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  React.useEffect(() => {
    if (pAgp) return;
    if (!patient_no) return;
    setAgp(() => []);
    requestReportAgpGraph({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
      search_dv: 0,
      search_key: 0,
    }, (success, data) => {
      if (success) {
        setAgp(() => data.agp_graph_list);
      }
    });
  }, [period_end, period_start, patient_no, setAgp, requestReportAgpGraph, pAgp]);

  const hash = React.useMemo(() => (pAgp || agp).reduce((a, c) => ({
    ...a,
    [c.time]: c,
  }), {}), [agp, pAgp]);

  React.useEffect(() => {
    const chart = new Chart(canvasRef.current, config([]));
    chartRef.current = chart;
    return () => {
      chart.destroy();
      delete chart.config;
    };
  }, []);

  React.useEffect(() => {
    const configuration = config(hash);
    configuration.data.datasets.forEach((dataset, datasetIndex) => {
      dataset.data.forEach((datum, datumIndex) => {
        chartRef.current.config.data.datasets[datasetIndex].data[datumIndex] = datum;
      });
    });
    chartRef.current.update();
  }, [hash]);

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const frameId = requestAnimationFrame(() => {
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);

    };
  }, [rect]);
  return (
    <VBox
      {...ex('SimulationChart', rest)}
    >
      <div {...ex('Chart', { ref })}>
        <canvas
          {...{
            ref: canvasRef,
            width: rect.width,
            height: rect.height,
          }}
        />
      </div>
    </VBox>
  );
}
